/**
 * Geocoding utilities using Nominatim API (OpenStreetMap).
 * Note: Nominatim requires an User-Agent header and has a usage policy (1 request per second).
 */

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';
const USER_AGENT = 'QPRO-Connect-App';

export interface GeocodeResult {
  lat: number;
  lng: number;
  city?: string;
  display_name: string;
}

/**
 * Reverse geocoding: coordinates to city/address.
 */
export async function reverseGeocode(lat: number, lng: number): Promise<GeocodeResult | null> {
  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
      {
        headers: {
          'User-Agent': USER_AGENT,
        },
      }
    );

    if (!response.ok) throw new Error('Geocoding service error');

    const data = await response.json();

    // Extract city, town, or village
    const city = data.address.city || data.address.town || data.address.village || data.address.municipality;

    return {
      lat,
      lng,
      city,
      display_name: data.display_name,
    };
  } catch (error) {
    console.error('Reverse geocoding failed:', error);
    return null;
  }
}

/**
 * Forward geocoding: city/address to coordinates.
 */
export async function geocode(query: string): Promise<GeocodeResult | null> {
  if (!query) return null;

  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/search?format=json&q=${encodeURIComponent(query)}&limit=1&addressdetails=1`,
      {
        headers: {
          'User-Agent': USER_AGENT,
        },
      }
    );

    if (!response.ok) throw new Error('Geocoding service error');

    const data = await response.json();

    if (data && data.length > 0) {
      const result = data[0];
      const city = result.address.city || result.address.town || result.address.village || result.address.municipality;

      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        city,
        display_name: result.display_name,
      };
    }

    return null;
  } catch (error) {
    console.error('Geocoding failed:', error);
    return null;
  }
}
